import { useMemo, useState } from 'react'
import { Link2, Package, Plus, Search, Shield, X } from 'lucide-react'
import { toast } from 'sonner'
import { MasterDetail } from '@/admin/components/shared/master-detail'
import {
  useProductCategories,
  useProductSearch,
  useProductTemplates,
} from '@/admin/hooks/use-products'
import {
  useCreateWarrantyMapping,
  useDeleteWarrantyMapping,
  useUpdateWarrantyMapping,
  useWarrantyMappings,
} from '@/admin/hooks/use-warranty-mappings'
import { useWarrantyTemplates } from '@/admin/hooks/use-warranties'
import type {
  MappingLevel,
  ProductWarrantyMapping,
  WarrantyTemplate,
  WarrantyTemplateOption,
} from '@/admin/types/warranties'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { SearchInput } from '@/shared/components/ui/search-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { toastApiError } from '@/shared/lib/api-error'
import { cn } from '@/shared/lib/utils'

type TemplateMapping = {
  mapping: ProductWarrantyMapping
  defaultOption: WarrantyTemplateOption | null
}

const WARRANTY_TYPE_LABELS: Record<string, string> = {
  MANUFACTURER_WARRANTY: 'Manufacturer',
  EXTENDED_WARRANTY: 'Extended',
  RETAILER_WARRANTY: 'Retailer',
}

const LEVEL_LABELS: Record<MappingLevel, string> = {
  GLOBAL: 'Organisation-wide',
  TEMPLATE: 'Product Template',
  CATEGORY: 'Category',
  PRODUCT: 'Product',
}

function canHaveDefault(template: WarrantyTemplate): boolean {
  return (
    template.warranty_type === 'MANUFACTURER_WARRANTY' ||
    template.warranty_type === 'RETAILER_WARRANTY'
  )
}

function targetName(mapping: ProductWarrantyMapping): string {
  switch (mapping.level) {
    case 'PRODUCT':
      return mapping.product_name ?? 'Unknown product'
    case 'CATEGORY':
      return mapping.category_name ?? 'Unknown category'
    case 'TEMPLATE':
      return mapping.product_template_name ?? 'Unknown template'
    case 'GLOBAL':
      return 'All products'
  }
}

export function WarrantyMappingsPage() {
  const { data: warrantyTemplates = [], isLoading: templatesLoading } =
    useWarrantyTemplates()
  const { data: mappings = [], isLoading: mappingsLoading } = useWarrantyMappings()

  const [search, setSearch] = useState('')
  const [selectedTemplateUuid, setSelectedTemplateUuid] = useState<string | null>(null)
  const [showAddDialog, setShowAddDialog] = useState(false)

  const mappingsByTemplate = useMemo(() => {
    const grouped = new Map<string, TemplateMapping[]>()
    for (const mapping of mappings) {
      for (const item of mapping.mapping_items) {
        const templateUuid = item.warranty_template.uuid
        if (!grouped.has(templateUuid)) grouped.set(templateUuid, [])
        grouped.get(templateUuid)?.push({
          mapping,
          defaultOption: item.default_option,
        })
      }
    }
    for (const [key, items] of grouped.entries()) {
      grouped.set(key, [...items].sort((a, b) => a.mapping.priority - b.mapping.priority))
    }
    return grouped
  }, [mappings])

  const filteredTemplates = useMemo(() => {
    const ordered = [...warrantyTemplates].sort((a, b) => a.name.localeCompare(b.name))
    if (!search.trim()) return ordered
    const query = search.trim().toLowerCase()
    return ordered.filter((template) => template.name.toLowerCase().includes(query))
  }, [search, warrantyTemplates])

  const selectedTemplate =
    filteredTemplates.find((template) => template.uuid === selectedTemplateUuid) ??
    warrantyTemplates.find((template) => template.uuid === selectedTemplateUuid) ??
    null

  const selectedMappings = selectedTemplate
    ? mappingsByTemplate.get(selectedTemplate.uuid) ?? []
    : []

  return (
    <>
      <div className="py-6" data-testid="warranty-mappings-page">
        <MasterDetail
          testId="warranty-mappings"
          title="Warranty Templates"
          hasSelection={!!selectedTemplate}
          selectedId={selectedTemplateUuid}
          visibleItemIds={filteredTemplates.map((template) => template.uuid)}
          onSelectionChange={setSelectedTemplateUuid}
          onBack={() => setSelectedTemplateUuid(null)}
          height="calc(100vh - 56px - 64px - 48px - 48px)"
          list={
            <div className="flex h-full flex-col gap-3">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search warranty templates..."
                iconSize={12}
                className="h-8 text-[12px]"
              />

              {templatesLoading || mappingsLoading ? (
                <p className="text-[12px] text-[var(--ink-3)]">Loading templates...</p>
              ) : filteredTemplates.length === 0 ? (
                <p className="text-[12px] text-[var(--ink-3)]">
                  {warrantyTemplates.length > 0
                    ? 'No warranty templates match your search.'
                    : 'No warranty templates available yet.'}
                </p>
              ) : (
                <div className="flex flex-1 flex-col gap-0.5 overflow-y-auto">
                  {filteredTemplates.map((template) => {
                    const isSelected = template.uuid === selectedTemplateUuid
                    const mappingCount = mappingsByTemplate.get(template.uuid)?.length ?? 0
                    return (
                      <Button
                        key={template.uuid}
                        type="button"
                        variant="ghost"
                        onClick={() => setSelectedTemplateUuid(template.uuid)}
                        data-testid={`warranty-mappings-item-${template.uuid}`}
                        className={cn(
                          'flex h-auto cursor-pointer items-center justify-between gap-2 rounded-[4px] px-3 py-2 text-left text-[12px] transition-all duration-150',
                          isSelected
                            ? 'bg-[rgba(28,90,72,0.06)] text-[var(--green)]'
                            : 'text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]',
                        )}
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <Shield className="h-3 w-3 shrink-0" />
                          <span className="truncate">{template.name}</span>
                        </div>
                        <span className="shrink-0 text-[10px] text-[var(--ink-3)]">
                          {mappingCount}
                        </span>
                      </Button>
                    )
                  })}
                </div>
              )}
            </div>
          }
          detail={
            selectedTemplate ? (
              <WarrantyTemplateMappingsDetail
                template={selectedTemplate}
                mappings={selectedMappings}
                onAddMapping={() => setShowAddDialog(true)}
              />
            ) : (
              <div className="flex justify-center pt-24">
                <p className="text-[13px] text-[var(--ink-3)]">
                  Select a warranty template to manage its mappings.
                </p>
              </div>
            )
          }
        />
      </div>

      {selectedTemplate ? (
        <AddMappingDialog
          defaultTemplate={selectedTemplate}
          existingMappings={mappings}
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
        />
      ) : null}
    </>
  )
}

function WarrantyTemplateMappingsDetail({
  template,
  mappings,
  onAddMapping,
}: {
  template: WarrantyTemplate
  mappings: TemplateMapping[]
  onAddMapping: () => void
}) {
  const deleteMapping = useDeleteWarrantyMapping()
  const updateMapping = useUpdateWarrantyMapping()
  const warrantyTypeLabel =
    WARRANTY_TYPE_LABELS[template.warranty_type] ?? template.warranty_type

  async function handleRemove(mapping: ProductWarrantyMapping) {
    try {
      if (mapping.mapping_items.length === 1) {
        await deleteMapping.mutateAsync(mapping.uuid)
      } else {
        await updateMapping.mutateAsync({
          uuid: mapping.uuid,
          mapping_items: mapping.mapping_items
            .filter((item) => item.warranty_template.uuid !== template.uuid)
            .map((item) => ({
              warranty_template_uuid: item.warranty_template.uuid,
              default_option_uuid: item.default_option?.uuid ?? null,
              display_order: item.display_order,
            })),
        })
      }
      toast.success('Mapping removed')
    } catch (err) {
      toastApiError(err, 'Failed to remove mapping')
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-[20px] text-[var(--ink)]">
            {template.name}
          </h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="h-auto text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
              {warrantyTypeLabel}
            </Badge>
            <Badge variant="secondary" className="h-auto text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
              {mappings.length} mapping{mappings.length === 1 ? '' : 's'}
            </Badge>
            {template.status !== 'ACTIVE' ? (
              <Badge className="h-auto bg-[var(--status-warn-bg)] text-[10px] text-[var(--status-warn)]">
                {template.status}
              </Badge>
            ) : null}
          </div>
        </div>

        <Button
          type="button"
          onClick={onAddMapping}
          className="flex h-auto cursor-pointer items-center gap-1.5 rounded-[4px] bg-[var(--green)] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)]"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Mapping
        </Button>
      </div>

      {mappings.length === 0 ? (
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] px-5 py-8 text-center">
          <Link2 className="mx-auto mb-2 h-5 w-5 text-[var(--ink-3)] opacity-40" />
          <p className="text-[12px] text-[var(--ink-3)]">
            This template has no warranty mappings yet.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)]">
          {mappings.map((item, index) => {
            const { mapping, defaultOption } = item
            return (
              <div
                key={mapping.uuid}
                className={cn(
                  'group flex items-center gap-3 px-4 py-3 transition-all duration-100 hover:bg-[var(--bg-subtle)]',
                  index < mappings.length - 1 && 'border-b-[0.5px] border-b-[var(--rule)]',
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] text-[var(--ink)]">{targetName(mapping)}</span>
                    <Badge variant="secondary" className="h-auto text-[10px]">
                      {LEVEL_LABELS[mapping.level]}
                    </Badge>
                  </div>
                  {defaultOption ? (
                    <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                      Default option: {defaultOption.display_name}
                    </p>
                  ) : null}
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => handleRemove(mapping)}
                  className="h-7 w-7 shrink-0 cursor-pointer rounded-[4px] text-[var(--ink-3)] opacity-0 transition-all duration-150 hover:bg-[rgba(220,38,38,0.08)] hover:text-[#dc2626] group-hover:opacity-100"
                  aria-label={`Remove mapping to ${targetName(mapping)}`}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function AddMappingDialog({
  defaultTemplate,
  existingMappings,
  open,
  onOpenChange,
}: {
  defaultTemplate: WarrantyTemplate
  existingMappings: ProductWarrantyMapping[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const createMapping = useCreateWarrantyMapping()
  const { data: warrantyTemplates = [] } = useWarrantyTemplates()
  const { data: productTemplates = [] } = useProductTemplates()
  const { data: categories = [] } = useProductCategories()
  const [level, setLevel] = useState<MappingLevel>('PRODUCT')
  const [productSearch, setProductSearch] = useState('')
  const [selectedTargetUuid, setSelectedTargetUuid] = useState<string | null>(null)
  const [selectedTargetName, setSelectedTargetName] = useState<string | null>(null)
  const [selectedTemplateUuid, setSelectedTemplateUuid] = useState<string | null>(null)
  const [defaultOptionUuid, setDefaultOptionUuid] = useState<string | null>(null)
  const { data: productResults } = useProductSearch(productSearch, {
    include_variants: true,
  })
  const selectedTemplate =
    warrantyTemplates.find((template) => template.uuid === selectedTemplateUuid) ?? null
  const showDefault = selectedTemplate
    ? canHaveDefault(selectedTemplate) && selectedTemplate.options.length > 0
    : false

  function reset() {
    setLevel('PRODUCT')
    setProductSearch('')
    setSelectedTargetUuid(null)
    setSelectedTargetName(null)
    setSelectedTemplateUuid(null)
    setDefaultOptionUuid(null)
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) reset()
    onOpenChange(nextOpen)
  }

  async function handleAdd() {
    if (!selectedTemplateUuid) {
      toast.error('Select a warranty template before creating the mapping')
      return
    }

    const existingMapping = existingMappings.find((mapping) => {
      if (level === 'GLOBAL') return mapping.level === 'GLOBAL'
      if (level === 'PRODUCT') {
        return mapping.level === 'PRODUCT' && mapping.product_uuid === selectedTargetUuid
      }
      if (level === 'CATEGORY') {
        return mapping.level === 'CATEGORY' && mapping.category_uuid === selectedTargetUuid
      }
      if (level === 'TEMPLATE') {
        return (
          mapping.level === 'TEMPLATE' &&
          mapping.product_template_uuid === selectedTargetUuid
        )
      }
      return false
    })

    const alreadyMapped = existingMapping?.mapping_items.some(
      (item) => item.warranty_template.uuid === selectedTemplateUuid,
    )

    if (alreadyMapped) {
      toast.error('This warranty template is already mapped to that target')
      return
    }

    try {
      const nextItem = {
        warranty_template_uuid: selectedTemplateUuid,
        default_option_uuid: defaultOptionUuid,
        display_order: existingMapping?.mapping_items.length ?? 0,
      }

      if (existingMapping) {
        await updateMappingForDialog(existingMapping, nextItem)
      } else {
        await createMapping.mutateAsync({
          level,
          product_uuid: level === 'PRODUCT' ? selectedTargetUuid : null,
          category_uuid: level === 'CATEGORY' ? selectedTargetUuid : null,
          product_template_uuid: level === 'TEMPLATE' ? selectedTargetUuid : null,
          mapping_items: [nextItem],
        })
      }
      toast.success('Mapping created')
      reset()
      onOpenChange(false)
    } catch (err) {
      toastApiError(err, 'Failed to create mapping')
    }
  }

  async function updateMappingForDialog(
    mapping: ProductWarrantyMapping,
    nextItem: {
      warranty_template_uuid: string
      default_option_uuid: string | null
      display_order: number
    },
  ) {
    await updateMapping.mutateAsync({
      uuid: mapping.uuid,
      mapping_items: [
        ...mapping.mapping_items.map((item) => ({
          warranty_template_uuid: item.warranty_template.uuid,
          default_option_uuid: item.default_option?.uuid ?? null,
          display_order: item.display_order,
        })),
        nextItem,
      ],
    })
  }

  const updateMapping = useUpdateWarrantyMapping()
  const canSubmit = (level === 'GLOBAL' || !!selectedTargetUuid) && !!selectedTemplateUuid

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Map warranty template</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div>
            <Label className="clara-section-label mb-1.5 block">Map to</Label>
            <Select
              value={level}
              onValueChange={(value) => {
                setLevel(value as MappingLevel)
                setSelectedTargetUuid(null)
                setSelectedTargetName(null)
                setProductSearch('')
              }}
            >
              <SelectTrigger className="w-full text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GLOBAL">All products (organisation-wide)</SelectItem>
                <SelectItem value="TEMPLATE">A product template</SelectItem>
                <SelectItem value="CATEGORY">A product category</SelectItem>
                <SelectItem value="PRODUCT">A specific product</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {level === 'PRODUCT' ? (
            <div>
              <Label className="clara-section-label mb-1.5 block">Product</Label>
              {selectedTargetUuid ? (
                <div className="flex items-center justify-between rounded-[4px] border-[0.5px] border-[var(--rule)] px-3 py-2">
                  <span className="text-[13px] text-[var(--ink)]">
                    {selectedTargetName}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => {
                      setSelectedTargetUuid(null)
                      setSelectedTargetName(null)
                      setProductSearch('')
                    }}
                    className="h-5 w-5 cursor-pointer text-[var(--ink-3)]"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-[var(--ink-3)]" />
                    <Input
                      value={productSearch}
                      onChange={(event) => setProductSearch(event.target.value)}
                      placeholder="Search products..."
                      className="pl-8 text-[13px]"
                    />
                  </div>
                  {productSearch.length >= 2 &&
                  (productResults?.results ?? []).length > 0 ? (
                    <div className="mt-2 flex max-h-[180px] flex-col gap-0.5 overflow-y-auto">
                      {(productResults?.results ?? []).map((product) => (
                        <Button
                          key={product.uuid}
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setSelectedTargetUuid(product.uuid)
                            setSelectedTargetName(
                              product.cached_long_name || product.cached_short_name,
                            )
                            setProductSearch('')
                          }}
                          className="flex h-auto cursor-pointer items-center justify-start gap-2 rounded-[4px] px-2 py-2 text-left text-[12px] text-[var(--ink)]"
                        >
                          <Package className="h-3 w-3 shrink-0 text-[var(--ink-3)]" />
                          {product.cached_long_name || product.cached_short_name}
                        </Button>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {level === 'TEMPLATE' ? (
            <div>
              <Label className="clara-section-label mb-1.5 block">
                Product Template
              </Label>
              <Select
                value={selectedTargetUuid ?? '__none__'}
                onValueChange={(value) => {
                  setSelectedTargetUuid(value === '__none__' ? null : value)
                  setSelectedTargetName(
                    productTemplates.find((productTemplate) => productTemplate.uuid === value)
                      ?.name ?? null,
                  )
                }}
              >
                <SelectTrigger className="w-full text-[13px]">
                  <SelectValue placeholder="Select template..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" disabled>
                    Select template...
                  </SelectItem>
                  {productTemplates.map((productTemplate) => (
                    <SelectItem key={productTemplate.uuid} value={productTemplate.uuid}>
                      {productTemplate.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {level === 'CATEGORY' ? (
            <div>
              <Label className="clara-section-label mb-1.5 block">
                Product Category
              </Label>
              <Select
                value={selectedTargetUuid ?? '__none__'}
                onValueChange={(value) => {
                  setSelectedTargetUuid(value === '__none__' ? null : value)
                  setSelectedTargetName(
                    categories.find((category) => category.uuid === value)?.name ?? null,
                  )
                }}
              >
                <SelectTrigger className="w-full text-[13px]">
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" disabled>
                    Select category...
                  </SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.uuid} value={category.uuid}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div>
            <Label className="clara-section-label mb-1.5 block">
              Warranty Template
            </Label>
            <Select
              value={selectedTemplateUuid ?? '__none__'}
              onValueChange={(value) => {
                setSelectedTemplateUuid(value === '__none__' ? null : value)
                setDefaultOptionUuid(null)
              }}
            >
              <SelectTrigger className="w-full text-[13px]">
                <SelectValue placeholder="Select warranty..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" disabled>
                  Select warranty...
                </SelectItem>
                {warrantyTemplates.map((warrantyTemplate) => (
                  <SelectItem key={warrantyTemplate.uuid} value={warrantyTemplate.uuid}>
                    {warrantyTemplate.name}
                    {warrantyTemplate.uuid === defaultTemplate.uuid ? ' (current)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showDefault ? (
            <div>
              <Label className="clara-section-label mb-1.5 block">
                Default Option
              </Label>
              <p className="mb-2 text-[11px] text-[var(--ink-3)]">
                Optionally set which option is pre-selected during registration.
              </p>
              <Select
                value={defaultOptionUuid ?? '__none__'}
                onValueChange={(value) =>
                  setDefaultOptionUuid(value === '__none__' ? null : value)
                }
              >
                <SelectTrigger className="w-full text-[13px]">
                  <SelectValue>
                    {defaultOptionUuid
                      ? selectedTemplate?.options.find(
                          (option) => option.uuid === defaultOptionUuid,
                        )
                          ?.display_name ?? 'Loading...'
                      : 'None'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {selectedTemplate?.options.map((option) => (
                    <SelectItem key={option.uuid} value={option.uuid}>
                      {option.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={!canSubmit || createMapping.isPending}
            data-testid="warranty-mappings-dialog-submit"
            className="h-auto cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)] disabled:opacity-40"
          >
            Add Mapping
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
